<template>
  <div ref="anchor" class="expandable-project-media" :style="expanded ? {height:`${inlineHeight}px`} : null">
    <component :is="disabled ? 'div' : 'dialog'" ref="viewer" class="project-media-viewer" :role="expanded ? 'dialog' : 'group'" :aria-modal="expanded ? true : undefined" :aria-label="title" :style="{'--media-ratio':aspectRatio}" @cancel.prevent="close" @close="restore" @click="closeOnBackdrop" @keydown.stop>
      <header v-if="expanded" class="project-media-head">
        <span>{{ title }}</span>
        <button class="project-media-close" type="button" aria-label="Close enlarged view" @click="close">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>
        </button>
      </header>
      <div class="project-media-screen demo-open-surface">
        <slot />
        <button v-if="!disabled" ref="trigger" type="button" class="project-media-zoom" :class="{'image-enlarge':!expanded && !pointerPreview, 'is-pointer-preview':!expanded && pointerPreview, 'is-corner-control':interactive}" :aria-label="`${expanded ? 'Reduce' : 'Enlarge'} ${title}`" :aria-haspopup="expanded ? undefined : 'dialog'" @click="expanded ? close() : open()"><svg v-if="interactive" viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5"/><path d="m12 12 5 5M6 8.5h5"/><path v-if="!expanded" d="M8.5 6v5"/></svg></button>
      </div>
      <div v-if="$slots.caption" class="project-media-caption"><slot name="caption" /></div>
    </component>
  </div>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
export default {
  props: { title: {type:String,required:true}, aspectRatio: {type:Number,default:16/9}, interactive: Boolean, disabled: Boolean, pointerPreview: Boolean },
  emits: ['change'],
  data: () => ({expanded:false,inlineHeight:0}),
  mounted() { window.addEventListener('hashchange',this.close); },
  beforeUnmount() { window.removeEventListener('hashchange',this.close); this.restore(); },
  methods: {
    async open() {
      if(this.disabled || this.expanded) return;
      this.inlineHeight=this.$refs.anchor.getBoundingClientRect().height;
      this._releaseScroll=lockBodyScroll();
      this.expanded=true;
      this.$emit('change',true);

      await this.$nextTick();
      this.$refs.viewer?.showModal();
      this.$refs.viewer?.querySelector('.project-media-close')?.focus({preventScroll:true});
    },
    close() { if(this.$refs.viewer?.open) this.$refs.viewer.close(); },
    restore() {
      if(!this.expanded) return;
      this.expanded=false;
      this._releaseScroll?.();
      this.$emit('change',false);
      this.$refs.trigger?.focus({preventScroll:true});
    },
    closeOnBackdrop(event) { if(event.target===this.$refs.viewer) this.close(); },
  },
};
</script>

<style scoped>
.expandable-project-media{min-width:0}
/* The same DOM stays mounted in the top layer, preserving video and WebGL state. */
.project-media-viewer{display:block;position:relative;inset:auto;box-sizing:border-box;width:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;overflow:visible;background:none;color:inherit}
.project-media-viewer[open]{position:fixed;inset:0;width:min(1600px,calc(100vw - 40px),calc((100dvh - 160px)*var(--media-ratio)));height:fit-content;max-height:calc(100dvh - 24px);margin:auto;background:var(--site-bg,#fff);color:var(--site-text-primary,#333);overflow:auto;overscroll-behavior:contain}
:global(body.dark-mode .project-media-viewer[open]){background:#242426;color:#e2e2e2;color-scheme:dark}
.project-media-viewer::backdrop{background:rgb(20 20 18 / 85%)}
.project-media-head{display:flex;align-items:center;justify-content:space-between;gap:20px;min-height:48px;padding:0 10px 0 18px;font:14px/1.4 'Quattrocento Sans',sans-serif}
.project-media-close{display:grid;place-items:center;flex:0 0 44px;width:44px;height:44px;padding:0;border:0;background:none;color:inherit;cursor:var(--site-interactive-cursor,pointer)}
.project-media-close svg{width:20px;height:20px;fill:none;stroke:currentColor;stroke-width:1.3}
.project-media-screen{position:relative}
.project-media-zoom{position:absolute;inset:0;z-index:2;width:100%;height:100%;padding:0;border:0;border-radius:0;background:none;cursor:var(--site-zoom-in-cursor,zoom-in)}
.project-media-zoom.is-pointer-preview{cursor:var(--site-interactive-cursor,pointer);outline:1px solid transparent;outline-offset:3px;transition:outline-color 140ms ease}
.project-media-zoom.is-pointer-preview:focus-visible{outline:2px solid var(--image-enlarge-focus);outline-offset:3px}
.project-media-viewer[open] .project-media-zoom{cursor:var(--site-zoom-out-cursor,zoom-out)}
.project-media-zoom.is-corner-control{inset:auto 8px 8px auto;z-index:4;width:36px;height:36px;display:grid;place-items:center;border-radius:50%;color:#fff;background:#292623d9}
.project-media-zoom.is-corner-control svg{width:19px;height:19px;fill:none;stroke:currentColor;stroke-width:1.2;stroke-linecap:round}
.project-media-close:focus-visible,.project-media-zoom:focus-visible{outline:2px solid currentColor;outline-offset:-4px}
.project-media-viewer[open] .project-media-caption{padding:0 16px 10px}
@media(hover:hover) and (pointer:fine){.project-media-close:hover{background:color-mix(in srgb,currentColor 9%,transparent)}}
@media(hover:hover) and (pointer:fine){.project-media-zoom.is-pointer-preview:hover{outline-color:var(--image-enlarge-rule)}.project-media-zoom.is-pointer-preview:focus-visible{outline-color:var(--image-enlarge-focus)}}
@media(prefers-reduced-motion:reduce){.project-media-zoom.is-pointer-preview{transition:none}}
@media(max-width:600px){.project-media-viewer[open]{width:calc(100vw - 16px)}.project-media-head{padding-left:12px;font-size:12px}.project-media-viewer[open] .project-media-caption{padding:0 10px 6px}}
</style>
